"use client";

import React, { useState, useEffect, useRef } from 'react';

const NAVY = '#071822';
const MINT = '#00d69f';

const BackToTopButton = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [scrollPct, setScrollPct] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const rafRef = useRef<number | null>(null);

    useEffect(() => {
        const onScroll = () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(() => {
                const scrolled = window.scrollY;
                const total = document.documentElement.scrollHeight - window.innerHeight;
                setScrollPct(total > 0 ? Math.min((scrolled / total) * 100, 100) : 0);
                setIsVisible(scrolled > 320);
            });
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, []);

    const scrollToTop = () => {
        // Custom eased scroll animation (easeInOutQuad)
        const start = window.scrollY;
        const duration = 1200;
        let startTime: number | null = null;

        const easeInOutQuad = (t: number) =>
            t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

        const step = (timestamp: number) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            window.scrollTo(0, start * (1 - easeInOutQuad(progress)));
            if (progress < 1) requestAnimationFrame(step);
        };

        requestAnimationFrame(step);
    };

    // Progress circle calculations
    const size = 52;
    const strokeWidth = 3;
    const radius = (size - strokeWidth * 2) / 2;
    const circumference = 2 * Math.PI * radius;
    const dashOffset = circumference - (scrollPct / 100) * circumference;

    return (
        <button
            onClick={scrollToTop}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            aria-label="Back to top"
            className="fixed bottom-24 right-6 z-[9999] rounded-full focus:outline-none cursor-pointer"
            style={{
                width: `${size}px`,
                height: `${size}px`,
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(16px)',
                pointerEvents: isVisible ? 'auto' : 'none',
                transition: 'opacity 0.3s ease, transform 0.3s ease',
            }}
        >
            {/* Progress ring */}
            <svg
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                className="absolute inset-0 pointer-events-none"
                style={{ transform: 'rotate(-90deg)' }}
            >
                <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(7, 24, 34, 0.12)" strokeWidth={strokeWidth} />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={MINT}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={dashOffset}
                    style={{ transition: 'stroke-dashoffset 0.2s ease' }}
                />
            </svg>

            {/* Button face: white, fills navy on hover (no movement or scaling) */}
            <div
                className="absolute inset-[5px] rounded-full flex items-center justify-center pointer-events-none"
                style={{
                    background: isHovered ? NAVY : '#FFFFFF',
                    boxShadow: '0 4px 14px rgba(7, 24, 34, 0.18)',
                    transition: 'background 0.2s ease',
                }}
            >
                <svg
                    width="18"
                    height="18"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke={isHovered ? MINT : NAVY}
                    strokeWidth={2.5}
                    style={{ transition: 'stroke 0.2s ease' }}
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                </svg>
            </div>
        </button>
    );
};

export default BackToTopButton;
